import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { doc, onSnapshot } from 'firebase/firestore';
import { db, httpsCallable, functions, updateFigmaInfoForMember, removeFigmaInfoForMember } from "../../firebase";
import MyHeader from "../../components/common/MyHeader";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import GoBackButton from "./components/GoBackButton";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import {
  PageWrapper, Container, Header, Title, Subtitle,
  Card, FinishWrapper, Footer, FooterText, FooterLink,
} from './components/setupStyles';


function FigmaSetupPage() {
  const { user, groupInfo, groupMembers, setGroupMembers } = useAuth();
  const navigate = useNavigate();
  const [connecting, setConnecting] = useState(false);
  const [showRemoveFigmaConfirm, setShowRemoveFigmaConfirm] = useState(false);

  useEffect(() => {
    if (!groupInfo?.groupId) return;

    const unsub = onSnapshot(doc(db, "Groups", groupInfo.groupId), (snap) => {
      if (!snap.exists()) return;
      setGroupMembers(snap.data()?.members || []);
    });

    return () => unsub();
  }, [groupInfo?.groupId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    // run only if we have a code and groupInfo is ready
    if (code && groupInfo?.groupId && user?.email) {
      handleExchangeFigma(code);
      // clean url immediately
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [groupInfo]);

  const handleExchangeFigma = async (code) => {
    try {
      setConnecting(true);
      const exchangeFigmaCode = httpsCallable(functions, "exchangeFigmaCode");
      const result = await exchangeFigmaCode({ code });
      const { access_token, refresh_token, expires_at, figma_user } = result.data;

      await updateFigmaInfoForMember(groupInfo.groupId, user.email, {
        ...figma_user,
        access_token,
        refresh_token,
        expires_at,
      });

      console.log("✅ figma linked");
    } catch (err) {
      console.error("figma auth failed:", err);
    } finally {
      setConnecting(false);
    }
  };

  const handleRemoveFigma = () => {
    if (!groupInfo?.groupId || !user?.email) return;
    setShowRemoveFigmaConfirm(true);
  };

  const confirmRemoveFigma = async () => {
    setShowRemoveFigmaConfirm(false);
    try {
      setConnecting(true);
      const result = await removeFigmaInfoForMember(groupInfo.groupId, user.email);
      if (result === true) {
        console.log("✅ Removed Figma connection");
      }
      else {
        console.error("❌ Failed to remove Figma connection");
      }
    } catch (err) {
      console.error("❌ Failed to remove Figma connection:", err);
    } finally {
      setConnecting(false);
    }
  };



  const handleConnectFigma = () => {
    const CLIENT_ID = process.env.REACT_APP_FIGMA_CLIENT_ID;
    const REDIRECT_URI = process.env.REACT_APP_FIGMA_REDIRECT_URI;
    const SCOPE = "current_user:read%20file_content:read%20file_metadata:read%20file_versions:read";
    const STATE = groupInfo.groupId;
    const url = `https://www.figma.com/oauth?client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(
      REDIRECT_URI
    )}&scope=${SCOPE}&state=${STATE}&response_type=code`;
    window.location.href = url;
  };

  const allConnected = groupMembers.length > 0 && groupMembers.every((m) => m.figma?.connected);

  return (
    <>
      <MyHeader />
      {showRemoveFigmaConfirm && (
        <ConfirmDialog
          title="Remove Figma Connection?"
          message="This will unlink your Figma account. You can reconnect at any time."
          confirmText="Yes, Remove"
          cancelText="No, Go Back"
          variant="danger"
          showIcon={true}
          onConfirm={confirmRemoveFigma}
          onCancel={() => setShowRemoveFigmaConfirm(false)}
        />
      )}
      <PageWrapper>

        <Container>
          <GoBackButton />
          <Header>
            <Title>Connect Your Figma Accounts</Title>
            <Subtitle>
              Each member must connect their Figma account before proceeding.
            </Subtitle>
          </Header>

          <Card>
            <UserList>
              {groupMembers.map((member, index) => (
                <React.Fragment key={member.email}>
                  <UserRow>
                    <LeftGroup>
                      <UserName>{member.name}</UserName>
                      <StatusGroup>
                        {member.figma ? (
                          <>
                            <BaseIcon className="green">
                              <FaCheckCircle />
                            </BaseIcon>
                            <Username>@{member.figma.handle}</Username>
                          </>
                        ) : (
                          <>
                            <BaseIcon className="red">
                              <MdCancel />
                            </BaseIcon>
                            <Status>Not Connected</Status>
                          </>
                        )}
                      </StatusGroup>
                    </LeftGroup>

                    {member.email === user.email && (
                      <ButtonGroup>
                        {member.figma ? (
                          <>
                            <RemoveButton
                              disabled={connecting}
                              onClick={handleRemoveFigma}
                            >
                              Remove
                            </RemoveButton>
                          </>
                        ) : (
                          <ConnectButton
                            disabled={connecting}
                            onClick={handleConnectFigma}
                          >
                            Connect Figma
                          </ConnectButton>
                        )}
                      </ButtonGroup>
                    )}
                  </UserRow>
                  {index < groupMembers.length - 1 && (
                    <Separator key={`separator-${index}`} />
                  )}
                </React.Fragment>

              ))}
            </UserList>




            {allConnected && (
              <FinishWrapper>
                <FinishButton onClick={() => navigate("/next-step")}>
                  All Connected! Continue →
                </FinishButton>
              </FinishWrapper>
            )}
          </Card>
          <Footer>
            <FooterText>
              Need help? Check out our{" "}
              <FooterLink
                href="https://docs.google.com/document/d/10ktlofLuFkFbs9ZEEs2jwpvycE6nz8KBXZyE4FJ8w40/edit?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
              >
                documentation
              </FooterLink>
            </FooterText>
          </Footer>
        </Container>
      </PageWrapper>
    </>
  );
}

export default FigmaSetupPage;


const UserName = styled.span`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  min-width: 8rem;
`;

const StatusGroup = styled.div`
  display: flex;
  align-items: center;
`;

const BaseIcon = styled.div`
  font-size: 1.5rem;
  display: inline-flex;
  align-items: center;
  &.red {
    color: #ef4444;
  }
  &.green {
    color: #22c55e;
  }
`;

const Status = styled.span`
  color: #ef4444;
  font-size: 0.875rem;
  margin-left: 0.25rem;
`;

const Username = styled.span`
  color: #15803d;
  font-size: 0.875rem;
  font-weight: 500;
  margin-left: 0.5rem;
`;

const ConnectButton = styled.button`
  background-color: #9333ea;
  color: white;
  font-weight: 600;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: #7e22ce;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;


const FinishButton = styled.button`
  background-color: #22c55e;
  color: white;
  font-weight: 600;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: #16a34a;
  }
`;


const Separator = styled.div`
  height: 1px;
  margin: 0.5rem 0;
  background: #d1d5db;
  opacity: 0.5;
`;

const UserList = styled.div`
`;


/* ----- User Rows ----- */

const UserRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-radius: 0.75rem;
  transition: all 0.25s ease;
  cursor: default;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem; /* space between name and status */
  flex-wrap: wrap; /* allows wrapping if narrow screen */
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const RemoveButton = styled.button`
  background-color: #ef4444;
  color: white;
  font-weight: 600;
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 0.6rem;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: #dc2626;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;